import { useContext } from 'react';
import { TenantContext } from '../../../context/tenant-context';
import { handleRequest } from '../quipli-service-client';
import {
  AdminCardRequest,
  StripeCard,
  CardRequest,
  ChargeHoldRequest,
  CreateRefundRequest,
  OrderPaymentStatus,
  PaymentMethodField,
  StripeConfig,
  StripeTerminal,
  StripeTransactionMode,
  TerminalLocation,
  Terminal,
  Payment,
} from './payment';
import { PaymentParsers } from './payment-parsers';
import { BankAccount, PaymentIntent, SetupIntent } from '@stripe/stripe-js';
import OrderPaymentStatusJson = PaymentParsers.OrderPaymentStatusJson;
import { StripeAccount } from '../payment-config/payment-config';
import { PaymentConfigParsers } from '../payment-config/payment-config-parsers';
import { UserAddress } from '../user/user';

export const usePaymentApiV1 = () => {
  const { tenant, contextPath } = useContext(TenantContext);

  async function createRefund(request: CreateRefundRequest): Promise<OrderPaymentStatus> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/v1/payments/refund`,
      headers: {
        'X-Tenant': tenant,
      },
      data: request,
    };
    const payment = await handleRequest<OrderPaymentStatusJson>(config);
    return PaymentParsers.toOrderPaymentStatus(payment);
  }

  async function chargeHold(request: ChargeHoldRequest): Promise<OrderPaymentStatus> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/v1/payments/chargeHold`,
      headers: {
        'X-Tenant': tenant,
      },
      data: request,
    };
    const payment = await handleRequest<OrderPaymentStatusJson>(config);
    return PaymentParsers.toOrderPaymentStatus(payment);
  }

  async function createPaymentIntent(
    amount: number,
    currencyCode: string,
    address: UserAddress,
  ): Promise<{ paymentIntent: PaymentIntent; url: string; payment: Payment }> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/customer-api/v2/payments/stripe`,
      headers: {
        'X-Tenant': tenant,
      },
      data: {
        amount,
        currencyCode,
        address,
      },
    };
    return await handleRequest<{ paymentIntent: PaymentIntent; url: string; payment: Payment }>(config);
  }

  async function completeAffirm(orderId: number, paymentId: string) {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/customer-api/v2/payments/stripe/completeAffirm`,
      headers: {
        'X-Tenant': tenant,
      },
      data: {
        orderId,
        paymentId,
      },
    };
    return await handleRequest(config);
  }

  async function getSetupIntent(bankAccount?: boolean): Promise<{ setupIntent: SetupIntent; url: string }> {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/customer-api/v1/payments/stripe/setupIntent${
        bankAccount ? '?bankAccount=true' : ''
      }`,
      headers: {
        'X-Tenant': tenant,
      },
    };
    return await handleRequest<{ setupIntent: SetupIntent; url: string }>(config);
  }

  async function getSetupIntentAdmin(
    userId: number,
    type: 'bankAccount' | 'card' | 'cardPresent',
  ): Promise<{ setupIntent: SetupIntent; url: string }> {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/v1/payments/stripe/setupIntent/${userId}?type=${type}`,
      headers: {
        'X-Tenant': tenant,
      },
    };
    return await handleRequest<{ setupIntent: SetupIntent; url: string }>(config);
  }

  async function saveCard(card: CardRequest): Promise<StripeCard> {
    const config = {
      method: 'put',
      url: `${contextPath}quipli-service/customer-api/v1/payments/stripe/cards`,
      headers: {
        'X-Tenant': tenant,
      },
      data: card,
    };

    const json = await handleRequest<PaymentParsers.StripeCardJson>(config);
    return PaymentParsers.toStripeCard(json);
  }

  async function saveCardAdmin(card: AdminCardRequest): Promise<StripeCard> {
    const config = {
      method: 'put',
      url: `${contextPath}quipli-service/v1/payments/stripe/cards`,
      headers: {
        'X-Tenant': tenant,
      },
      data: card,
    };

    const json = await handleRequest<PaymentParsers.StripeCardJson>(config);
    return PaymentParsers.toStripeCard(json);
  }

  async function getCardsForUser(
    userId: number,
    includeDeleted?: boolean,
    setupIntentId?: string,
    cardId?: string,
  ): Promise<StripeCard[]> {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/v1/payments/stripe/cards`,
      params: {
        userId,
        includeDeleted,
        setupIntentId,
        cardId,
      },
      headers: {
        'X-Tenant': tenant,
      },
    };

    const json = await handleRequest<PaymentParsers.StripeCardJson[]>(config);
    return json.map(PaymentParsers.toStripeCard);
  }

  async function getCardById(cardId: number): Promise<StripeCard> {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/v1/payments/stripe/cards/${cardId}`,
      headers: {
        'X-Tenant': tenant,
      },
    };

    const card = await handleRequest<PaymentParsers.StripeCardJson>(config);
    return PaymentParsers.toStripeCard(card);
  }

  async function getStripeSettings(): Promise<StripeConfig> {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/customer-api/v1/payments/payment-method/Stripe`,
      headers: {
        'X-Tenant': tenant,
      },
    };
    const fields = await handleRequest<PaymentMethodField[]>(config);
    if (!fields) {
      throw new Error('Could not get fields');
    }
    return {
      publishableKey: fields.find((f) => f.key == 'publishableKey')!.value,
      connectedAccountId: fields.find((f) => f.key == 'connectedAccountId')!.value,
      transactionMode: fields.find((f) => f.key == 'transaction_mode')!.value as StripeTransactionMode,
      accountType: fields.find((f) => f.key == 'accountType')!.value,
    };
  }

  async function getStripeSettingsAdmin(): Promise<StripeConfig> {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/v1/payments/payment-method/Stripe`,
      headers: {
        'X-Tenant': tenant,
      },
    };
    const fields = await handleRequest<PaymentMethodField[]>(config);
    if (!fields) {
      throw new Error('Could not get fields');
    }
    return {
      publishableKey: fields.find((f) => f.key == 'publishableKey')!.value,
      connectedAccountId: fields.find((f) => f.key == 'connectedAccountId')!.value,
      transactionMode: fields.find((f) => f.key == 'transaction_mode')!.value as StripeTransactionMode,
      accountType: fields.find((f) => f.key == 'accountType')!.value,
    };
  }

  async function addTerminalCard(
    userId: number,
    locationId: number,
    terminalId?: string,
    orderId?: number,
  ): Promise<{ setupIntentId: string; status: string }> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/v1/payments/stripe/terminal/${locationId}/${userId}`,
      headers: {
        'X-Tenant': tenant,
      },
      data: {
        terminalId,
        orderId,
      },
    };
    return await handleRequest<{ setupIntentId: string; status: string }>(config);
  }

  async function completeCardSetup(userId: number, setupIntentId: string, orderId?: number): Promise<StripeCard> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/v1/payments/stripe/terminal/completeSetup`,
      data: { userId, setupIntentId, orderId },
      headers: {
        'X-Tenant': tenant,
      },
    };
    return await handleRequest<StripeCard>(config);
  }

  async function cancelCardSetup(setupIntentId: string): Promise<void> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/v1/payments/stripe/terminal/cancel`,
      data: { setupIntentId },
      headers: {
        'X-Tenant': tenant,
      },
    };
    return await handleRequest<void>(config);
  }

  async function listTerminals(locationId: number): Promise<StripeTerminal[]> {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/v1/payments/stripe/terminals/${locationId}`,
      headers: {
        'X-Tenant': tenant,
      },
    };
    return await handleRequest<StripeTerminal[]>(config);
  }

  async function addTerminalCardUser(locationId: number): Promise<{ setupIntentId: string; status: string }> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/customer-api/v1/payments/stripe/terminal/${locationId}`,
      headers: {
        'X-Tenant': tenant,
      },
    };
    return await handleRequest<{ setupIntentId: string; status: string }>(config);
  }

  async function completeCardSetupUser(setupIntentId: string): Promise<{ setupIntentId: string; status: string }> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/customer-api/v1/payments/stripe/terminal/completeSetup`,
      data: { setupIntentId },
      headers: {
        'X-Tenant': tenant,
      },
    };
    return await handleRequest<{ setupIntentId: string; status: string }>(config);
  }

  async function getSetupAttemptStatusUser(setupIntentId: string): Promise<{ id: string; status: string }> {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/customer-api/v1/payments/stripe/terminal/attempt?setupIntent=${setupIntentId}`,
      headers: {
        'X-Tenant': tenant,
      },
    };
    return await handleRequest<{ id: string; status: string }>(config);
  }

  async function getSetupAttemptStatusAdmin(
    userId: number,
    setupIntentId: string,
  ): Promise<{ id: string; status: string }> {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/v1/payments/stripe/terminal/attempt?userId=${userId}&setupIntent=${setupIntentId}`,
      headers: {
        'X-Tenant': tenant,
      },
    };
    return await handleRequest<{ id: string; status: string }>(config);
  }

  async function createAccountSession(accountId: string): Promise<string> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/v1/payment-config/stripe/accountSession`,
      data: { accountId },
      headers: {
        'X-Tenant': tenant,
      },
    };

    const result = await handleRequest<{ clientSecret: string }>(config);
    return result.clientSecret;
  }

  async function completeCustomSetup(accountId: string): Promise<StripeAccount> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/v1/payment-config/stripe/completeCustomSetup`,
      data: { accountId },
      headers: {
        'X-Tenant': tenant,
      },
    };

    const result = await handleRequest<PaymentConfigParsers.StripeAccountJson>(config);
    return PaymentConfigParsers.toStripeAccount(result);
  }

  async function deleteCardAdmin(cardId: number, userId: number): Promise<StripeCard> {
    const config = {
      method: 'delete',
      url: `${contextPath}quipli-service/v1/payments/stripe/cards/${cardId}`,
      headers: {
        'X-Tenant': tenant,
      },
      data: {
        userId,
      },
    };
    const card = await handleRequest<PaymentParsers.StripeCardJson>(config);
    return PaymentParsers.toStripeCard(card);
  }

  async function createConnectSetupIntent(): Promise<SetupIntent> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/v1/payment-config/stripe/connectSetupIntent`,
      headers: {
        'X-Tenant': tenant,
      },
    };

    return await handleRequest<SetupIntent>(config);
  }

  async function getBankDetails(): Promise<BankAccount[]> {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/v1/payment-config/stripe/bankDetails`,
      headers: {
        'X-Tenant': tenant,
      },
    };

    return await handleRequest<BankAccount[]>(config);
  }

  async function getTerminalLocations(): Promise<TerminalLocation[]> {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/v1/payment-config/stripe/terminal/locations`,
      headers: {
        'X-Tenant': tenant,
      },
    };

    return await handleRequest<TerminalLocation[]>(config);
  }

  async function getTerminalsForLocations(locationIds: string[]): Promise<Terminal[]> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/v1/payment-config/stripe/terminal/locations/`,
      headers: {
        'X-Tenant': tenant,
      },
      data: {
        locationIds,
      },
    };
    return await handleRequest<Terminal[]>(config);
  }

  async function getTerminal(terminalId: string): Promise<Terminal> {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/v1/payment-config/stripe/terminal/${terminalId}`,
      headers: {
        'X-Tenant': tenant,
      },
    };

    return await handleRequest<Terminal>(config);
  }

  async function addTerminal(locationId: number, pairingCode: string, label: string): Promise<Terminal> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/v1/payment-config/stripe/terminal/locations/${locationId}`,
      headers: {
        'X-Tenant': tenant,
      },
      data: {
        label,
        pairingCode,
      },
    };

    return await handleRequest<Terminal>(config);
  }

  async function updateTerminal(terminalId: string, label: string): Promise<Terminal> {
    const config = {
      method: 'put',
      url: `${contextPath}quipli-service/v1/payment-config/stripe/terminal/${terminalId}`,
      headers: {
        'X-Tenant': tenant,
      },
      data: {
        label,
      },
    };

    return await handleRequest<Terminal>(config);
  }

  return {
    createRefund,
    chargeHold,
    createPaymentIntent,
    getSetupIntent,
    getStripeSettings,
    saveCard,
    getSetupIntentAdmin,
    saveCardAdmin,
    getCardsForUser,
    getCardById,
    getStripeSettingsAdmin,
    addTerminalCard,
    completeCardSetup,
    cancelCardSetup,
    listTerminals,
    addTerminalCardUser,
    completeCardSetupUser,
    getSetupAttemptStatusUser,
    getSetupAttemptStatusAdmin,
    createAccountSession,
    completeCustomSetup,
    deleteCardAdmin,
    createConnectSetupIntent,
    getBankDetails,
    addTerminal,
    updateTerminal,
    getTerminal,
    getTerminalLocations,
    getTerminalsForLocations,
    completeAffirm,
  };
};
